import { apiRequest } from "./apiClient";

/**
 * Fetch a clinic's outreach treatment whitelist (the procedures a campaign may
 * offer). Admins may pass a clinicId to read another clinic; non-admins are
 * scoped to their own clinic server-side. Returns every row including inactive
 * ones — filter on `is_active` for selection.
 *
 * @param {Object} [opts]
 * @param {string} [opts.clinicId] - Admin-only clinic override.
 * @returns {Promise<Array<{id, clinic_id, code, display_name, default_duration_minutes, is_active}>>}
 */
export const fetchOutreachTreatments = async ({ clinicId } = {}) => {
  const endpoint = clinicId
    ? `/outreach/treatments?clinic_id=${encodeURIComponent(clinicId)}`
    : "/outreach/treatments";

  const response = await apiRequest({ method: "GET", endpoint });
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
};

/**
 * Add a procedure the clinic can offer on a campaign call.
 * A duplicate active code is a 409.
 *
 * @param {Object} fields - { clinicId?, code, displayName, durationMinutes, nexhealthAppointmentTypeId? }
 * @returns {Promise<Object|null>} the created treatment row
 */
export const createOutreachTreatment = async ({
  clinicId,
  code,
  displayName,
  durationMinutes,
  nexhealthAppointmentTypeId,
}) => {
  const body = {
    code,
    display_name: displayName,
    default_duration_minutes: durationMinutes,
  };
  if (clinicId) body.clinic_id = clinicId;
  if (nexhealthAppointmentTypeId) {
    body.nexhealth_appointment_type_id = nexhealthAppointmentTypeId;
  }
  const response = await apiRequest({
    method: "POST",
    endpoint: "/outreach/treatments",
    body,
  });
  return response?.data ?? null;
};

/**
 * Change a procedure. The code is fixed once created: campaigns and outreach
 * payloads refer to it, and the API rejects the key outright even unchanged.
 * `isActive: false` is the soft delete; there is no DELETE.
 *
 * @param {string} treatmentId
 * @param {Object} fields - { clinicId?, displayName?, durationMinutes?, isActive?, nexhealthAppointmentTypeId? }
 * @returns {Promise<Object|null>} the updated treatment row
 */
export const updateOutreachTreatment = async (treatmentId, {
  clinicId,
  displayName,
  durationMinutes,
  isActive,
  nexhealthAppointmentTypeId,
}) => {
  const body = {};
  if (clinicId) body.clinic_id = clinicId;
  if (displayName !== undefined) body.display_name = displayName;
  if (durationMinutes !== undefined) body.default_duration_minutes = durationMinutes;
  if (isActive !== undefined) body.is_active = isActive;
  if (nexhealthAppointmentTypeId !== undefined) {
    body.nexhealth_appointment_type_id = nexhealthAppointmentTypeId;
  }
  const response = await apiRequest({
    method: "PATCH",
    endpoint: `/outreach/treatments/${treatmentId}`,
    body,
  });
  return response?.data ?? null;
};
